import {
  Component,
  ComponentProps,
  createComputed,
  createSignal,
  JSX,
  on,
  runWithOwner,
  Show,
} from "solid-js";
import { createStore } from "solid-js/store";
import { Dynamic } from "solid-js/web";
import c from "class-c";
import { Motion, Presence } from "solid-motionone";
import { createBoundSignal } from "solid-signals";
import { Event } from "solid-u";

import UObject from "@repo/utils/UObject";
import UPromise from "@repo/utils/UPromise";
import createPropsProvider from "@repo/utils-solid/createPropsProvider";
import UOwner from "@repo/utils-solid/UOwner";

import CheckAnimation from "../animation/CheckAnimation";
import Delay from "../animation/Delay";
import Expand from "../animation/Expand";
import { useError } from "../error/ErrorProvider";
import Spinner from "../loaders/Spinner";
import createDomRect from "../signals/createDomRect";
import createInitial from "../signals/createInitial";

import styles from "./LoadableButton.module.scss";

type IntrinsicElementsWith<Filter> = {
  [Key in keyof JSX.IntrinsicElements]: JSX.IntrinsicElements[Key] extends Filter
    ? Key
    : never;
}[keyof JSX.IntrinsicElements];

declare namespace LoadableButton {
  type Status = "initial" | "pending" | "success";
  type AsProps = {
    class?: string;
    onClick?: JSX.EventHandlerUnion<
      // HTMLElement doesn't match HTMLDivElement
      any,
      MouseEvent
    >;
    children?: JSX.Element;
  };

  type As = ComponentProps<IntrinsicElementsWith<AsProps>> | AsProps;

  type Props<As extends LoadableButton.As = any> = UObject.Override<
    As,
    {
      as: Component<As>;
      class?: string;
      // Prop signal change will trigger state reset
      reset?: any;
      status?: LoadableButton.Status;
      onClick(
        e: Event.FromHandlerUnion<
          (As extends { onClick?: JSX.EventHandlerUnion<any, MouseEvent> }
            ? As
            : never)["onClick"]
        > & {
          setPendingText(text?: string): void;
        }
      ): unknown | Promise<unknown>;
      text?: { pending?: string; success?: string };
      pendingOnly?: boolean;
      resize?: boolean;
    }
  >;
}

function LoadableButton<As extends LoadableButton.As>(
  _props: LoadableButton.Props<As>
) {
  const {
    as: _as,
    class: className,
    text,
    children,
    status: _status,
    onClick,
    pendingOnly,
    reset,
    resize,
    ...props
  } = LoadableButton.PropsProvider.useMerge(_props) satisfies D;

  const error = useError();

  const [status, setStatus] = createBoundSignal<LoadableButton.Status>(
    "initial",
    [_status && (() => _status), () => {}]
  );
  const [pendingText, setPendingText] = createSignal<string>();
  const initial = createInitial();

  // Need to watch for some refs in animation, must be reactive
  const [refs, setRefs] = createStore({ pending: {}, success: {} } as {
    main: HTMLDivElement;
    pending: {
      spinner: HTMLDivElement;
      text: HTMLDivElement;
    };
    success: {
      check: HTMLDivElement | undefined;
      text: HTMLDivElement | undefined;
    };
  });

  return (
    // @ts-expect-error
    <Dynamic
      component={_as}
      {...props}
      class={c`${styles["loadable-button"]} ${className}`}
      onClick={(e: any) => {
        if (_status || status() !== "initial") return;

        const owner = UOwner.makeDetached();

        (async () => {
          const res = onClick(Object.assign(e, { setPendingText }));
          if (!(res instanceof Promise)) return;

          setStatus("pending");

          let wasReset = false;
          runWithOwner(owner, () =>
            createComputed(
              on(
                () => reset,
                () => {
                  setStatus("initial");
                  wasReset = true;
                },
                { defer: true }
              )
            )
          );

          await res
            .then(() => {
              if (wasReset) return;
              setStatus("success");
              return UPromise.delay(LoadableButton.successDuration);
            })
            .finally(() => {
              setStatus("initial");
            });
        })()
          .catch((e) => {
            if (e != null) error.capture(e);
          })
          .finally(() => {
            owner.dispose();
            setPendingText();
          });
      }}
    >
      {(() => {
        const opacity = () =>
          status() === "initial" || !!(pendingOnly && status() === "pending")
            ? 1
            : 0;

        return (
          <Show
            when={resize}
            fallback={
              <main
                style={{
                  opacity: opacity(),
                  transition: "0.3s",
                }}
              >
                {children}
              </main>
            }
          >
            {(() => {
              const contentRect = createDomRect<HTMLDivElement>();

              return (
                <Motion.main
                  ref={(el) => setRefs("main", el)}
                  animate={{
                    opacity: +(status() === "initial"),
                    width: `${Math.max(
                      contentRect()?.width || 0,
                      (() => {
                        if (initial()) return 0;
                        if (status() === "pending")
                          return (
                            refs.pending.spinner.clientWidth +
                            refs.pending.text.clientWidth
                          );
                        if (status() === "success")
                          return (
                            (refs.success.check?.clientWidth || 0) +
                            (refs.success.text?.scrollWidth || 0)
                          );
                      })() || 0
                    )}px`,
                  }}
                >
                  <div ref={contentRect.ref} class="fit-content">
                    {children}
                  </div>
                </Motion.main>
              );
            })()}
          </Show>
        );
      })()}
      <Presence>
        {status() === "pending" && (
          <Motion.div
            class={styles.pending}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Spinner ref={(el) => setRefs("pending", "spinner", el)} />
            <span class="pl-0.5" ref={(el) => setRefs("pending", "text", el)}>
              {pendingText() || text?.pending || "Loading..."}
            </span>
          </Motion.div>
        )}
        {status() === "success" && !pendingOnly && (
          <Motion.div
            class={styles.success}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Delay for={300}>
              <CheckAnimation ref={(el) => setRefs("success", "check", el)} />
              <Delay for={300}>
                <Expand direction="x">
                  <div
                    class="pl-0.5"
                    ref={(el) => setRefs("success", "text", el)}
                  >
                    {text?.success || "Success"}
                  </div>
                </Expand>
              </Delay>
            </Delay>
          </Motion.div>
        )}
      </Presence>
    </Dynamic>
  );
}

LoadableButton.successDuration = 1200;
LoadableButton.afterSuccess = (fn: () => void) => {
  setTimeout(fn, LoadableButton.successDuration);
};

LoadableButton.PropsProvider = createPropsProvider<LoadableButton.Props>(
  "LoadableButton",
  { text: (context, props) => ({ ...context.text, ...props.text }) }
);

export default LoadableButton;
